import React from 'react';

import { Box } from '@mui/material';

import { SectionHeading } from './SectionHeading';
import { classes } from './styles';

interface AboutUsProps {
  body?: string;
  aboutUsContent?: string;
  sectionTitle?: string;
  videoLink?: string;
  imageUrl?: string;
  fontFamily?: string;
}

const ABOUT_US_HEADING = 'About Us';

const aboutUsHeading = (sectionTitle?: string): string => {
  const trimmed = sectionTitle?.trim();
  if (trimmed) return trimmed;
  return ABOUT_US_HEADING;
};

const extractVideoId = (url: string): string | null => {
  const vimeoMatch = /(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/.exec(url);
  if (vimeoMatch) return vimeoMatch[1];

  const youtubeMatch =
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/.exec(url);
  if (youtubeMatch) return youtubeMatch[1];

  return null;
};

const renderVideo = (videoLink: string, heading: string, fontFamily?: string) => {
  const videoId = extractVideoId(videoLink);
  if (!videoId) {
    return (
      <Box sx={classes.aboutUs.videoContainerSx()}>
        <Box sx={classes.aboutUs.invalidVideoFallbackSx(fontFamily)}>Invalid video URL</Box>
      </Box>
    );
  }

  const isVimeo = videoLink.includes('vimeo');
  const embedUrl = isVimeo
    ? `https://player.vimeo.com/video/${videoId}`
    : `https://www.youtube.com/embed/${videoId}`;

  return (
    <Box sx={classes.aboutUs.videoContainerSx()}>
      <iframe
        allowFullScreen
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        src={embedUrl}
        style={classes.aboutUs.videoStyle()}
        title={`${heading} video`}
      />
    </Box>
  );
};

const renderImage = (imageUrl: string) => (
  <Box sx={classes.aboutUs.imageContainerSx()}>
    <Box alt="" component="img" src={imageUrl} sx={classes.aboutUs.imageSx()} />
  </Box>
);

export const AboutUs: React.FC<AboutUsProps> = ({
  body,
  aboutUsContent,
  sectionTitle,
  videoLink,
  imageUrl,
  fontFamily
}) => {
  const content = body ?? aboutUsContent;
  const heading = aboutUsHeading(sectionTitle);

  if (!content && !videoLink && !imageUrl) {
    return null;
  }

  return (
    <Box aria-labelledby="about-us-heading" component="section" sx={classes.aboutUs.containerSx()}>
      <Box sx={classes.aboutUs.innerContainerSx()}>
        <Box sx={classes.aboutUs.contentSx()}>
          <SectionHeading fontFamily={fontFamily} id="about-us-heading" variant="compact">
            {heading}
          </SectionHeading>

          <Box sx={classes.aboutUs.bodySx()}>
            {content ? (
              <Box sx={classes.aboutUs.textColumnSx()}>
                <Box
                  className="wcag-rich-text"
                  dangerouslySetInnerHTML={{ __html: content }}
                  sx={classes.aboutUs.htmlContentSx(fontFamily)}
                />
              </Box>
            ) : null}

            {videoLink ? renderVideo(videoLink, heading, fontFamily) : null}
            {imageUrl && !videoLink ? renderImage(imageUrl) : null}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AboutUs;
